import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import AdminLayout from "../../admin/AdminLayout";
import AdminAuthGuard from "../../admin/AdminAuthGuard";
import { api, getToken } from "../../api/client";
import { TestTube, MapPin, Clock, Upload, CheckCircle2, FlaskConical, FileDown, User } from "lucide-react";

const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminLabOrderDetail = () => {
    const { id } = useParams();
    const [lab, setLab] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef(null);

    const load = () => api.get(`/admin/lab-orders?status=&page_size=200`).then((r) => {
        const found = (r.data.items || []).find((x) => x.id === id);
        setLab(found || null);
    }).finally(() => setLoading(false));
    useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    const action = async (path, label) => {
        setBusy(true);
        try {
            await api.post(`/admin/lab-orders/${id}/${path}`);
            toast.success(label);
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail?.message || err?.response?.data?.detail?.code || "Action failed");
        } finally { setBusy(false); }
    };

    const uploadReport = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const ext = file.name.split(".").pop().toLowerCase();
        if (!["pdf", "jpg", "jpeg", "png"].includes(ext)) {
            toast.error("Only PDF / JPG / PNG accepted"); return;
        }
        if (file.size > 10 * 1024 * 1024) { toast.error("Max 10 MB"); return; }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const token = getToken();
            const res = await fetch(`${BACKEND_URL}/api/admin/lab-orders/${id}/upload-report`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
                body: fd,
            });
            if (!res.ok) {
                const j = await res.json().catch(() => ({}));
                throw new Error(j?.detail?.message || j?.detail?.code || "Upload failed");
            }
            toast.success("Report uploaded · patient notified");
            await load();
        } catch (err) {
            toast.error(err.message || "Upload failed");
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    if (loading) return (<AdminAuthGuard><AdminLayout title="Lab order"><p className="text-sm text-zinc-500">Loading…</p></AdminLayout></AdminAuthGuard>);
    if (!lab) return (<AdminAuthGuard><AdminLayout title="Lab order"><p className="text-sm text-zinc-500">Not found. <Link to="/admin/lab-orders" className="text-brand-700 hover:underline">Back to list</Link></p></AdminLayout></AdminAuthGuard>);

    const cobj = lab.collection || {};
    const addr = cobj.address;

    const canCollect = lab.status === "confirmed";
    const canProcess = lab.status === "sample_collected";
    const canUpload = ["processing", "sample_collected"].includes(lab.status);

    return (
        <AdminAuthGuard>
            <AdminLayout title={lab.lab_no} subtitle={`${lab.user_name || lab.user_email || "Patient"} • placed ${new Date(lab.created_at).toLocaleString()}`}>
                <Link to="/admin/lab-orders" className="text-xs text-zinc-500 hover:underline">← All lab orders</Link>

                <div className="mt-4 grid gap-4 lg:grid-cols-3">
                    {/* Left */}
                    <div className="lg:col-span-2 space-y-4">
                        <section className="rounded-2xl border border-zinc-100 bg-white p-5" data-testid="admin-lab-items">
                            <p className="label-uppercase text-zinc-500">Items</p>
                            <ul className="mt-3 space-y-2">
                                {(lab.items || []).map((it, i) => (
                                    <li key={i} className="flex items-start justify-between gap-2 text-sm">
                                        <span className="flex items-start gap-2"><TestTube className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand-700" />{it.test_name || it.package_name}</span>
                                        <span className="font-medium text-zinc-900">{inr(it.selling_price)}</span>
                                    </li>
                                ))}
                            </ul>
                            <div className="mt-3 border-t border-zinc-100 pt-3 text-xs">
                                <p className="flex justify-between text-zinc-500"><span>Subtotal</span><span>{inr(lab.subtotal)}</span></p>
                                {Number(lab.elite_discount || 0) > 0 && <p className="flex justify-between text-amber-700"><span>Elite discount</span><span>− {inr(lab.elite_discount)}</span></p>}
                                <p className="flex justify-between border-t border-zinc-100 pt-2 text-sm font-semibold text-zinc-900"><span>Total</span><span>{inr(lab.total)}</span></p>
                            </div>
                        </section>

                        <section className="rounded-2xl border border-zinc-100 bg-white p-5" data-testid="admin-lab-history">
                            <p className="label-uppercase text-zinc-500">History</p>
                            <ol className="mt-3 space-y-2 text-xs">
                                {(lab.history || []).map((h, i) => (
                                    <li key={i} className="flex items-start gap-2">
                                        <span className="mt-0.5 inline-block h-1.5 w-1.5 rounded-full bg-brand-500" />
                                        <span className="text-zinc-500">{new Date(h.at).toLocaleString()}</span>
                                        <span className="text-zinc-700">{h.event.replace(/_/g, " ")}</span>
                                    </li>
                                ))}
                            </ol>
                        </section>
                    </div>

                    {/* Right */}
                    <aside className="space-y-4">
                        <section className="rounded-2xl border border-zinc-100 bg-white p-4">
                            <p className="label-uppercase text-zinc-500">Status</p>
                            <p className="mt-2 inline-flex rounded-full bg-brand-50 px-2.5 py-1 text-xs font-medium text-brand-700">{(lab.status || "").replace("_", " ")}</p>

                            <div className="mt-4 space-y-2" data-testid="admin-lab-actions">
                                {canCollect && (
                                    <button onClick={() => action("mark-collected", "Sample marked collected")} disabled={busy} className="inline-flex w-full items-center justify-center gap-1.5 rounded-md bg-violet-600 px-3 py-2 text-xs font-medium text-white hover:bg-violet-700 disabled:opacity-50" data-testid="admin-lab-collect-btn">
                                        <CheckCircle2 className="h-3.5 w-3.5" /> Mark sample collected
                                    </button>
                                )}
                                {canProcess && (
                                    <button onClick={() => action("mark-processing", "Marked processing")} disabled={busy} className="inline-flex w-full items-center justify-center gap-1.5 rounded-md bg-blue-600 px-3 py-2 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50" data-testid="admin-lab-process-btn">
                                        <FlaskConical className="h-3.5 w-3.5" /> Move to processing
                                    </button>
                                )}
                                {canUpload && (
                                    <label className="inline-flex w-full cursor-pointer items-center justify-center gap-1.5 rounded-md bg-emerald-700 px-3 py-2 text-xs font-medium text-white hover:bg-emerald-800">
                                        <Upload className="h-3.5 w-3.5" /> {uploading ? "Uploading…" : "Upload report (PDF/JPG)"}
                                        <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={uploadReport} className="hidden" data-testid="admin-lab-upload-input" />
                                    </label>
                                )}
                                {lab.status === "report_ready" && lab.report_url && (
                                    <a href={`${BACKEND_URL}${lab.report_url}`} target="_blank" rel="noreferrer" className="inline-flex w-full items-center justify-center gap-1.5 rounded-md border border-emerald-200 bg-white px-3 py-2 text-xs font-medium text-emerald-700 hover:bg-emerald-50" data-testid="admin-lab-view-report-btn">
                                        <FileDown className="h-3.5 w-3.5" /> View uploaded report
                                    </a>
                                )}
                            </div>
                        </section>

                        <section className="rounded-2xl border border-zinc-100 bg-white p-4">
                            <p className="label-uppercase text-zinc-500">Patient</p>
                            <p className="mt-2 inline-flex items-center gap-1.5 font-medium text-zinc-900"><User className="h-4 w-4" />{lab.patient_details?.full_name}</p>
                            <p className="text-xs text-zinc-500">{lab.patient_details?.age} yrs · {lab.patient_details?.gender}</p>
                            <p className="text-xs text-zinc-500">{lab.patient_details?.phone}</p>
                        </section>

                        <section className="rounded-2xl border border-zinc-100 bg-white p-4">
                            <p className="label-uppercase text-zinc-500">Collection</p>
                            <p className="mt-2 text-xs text-zinc-700"><Clock className="mr-1 inline h-3 w-3" />{cobj.scheduled_at ? new Date(cobj.scheduled_at).toLocaleString() : "—"}</p>
                            {cobj.type === "home" && addr ? (
                                <p className="mt-1 text-xs text-zinc-500"><MapPin className="mr-0.5 inline h-3 w-3" />{[addr.line1, addr.line2, addr.city, addr.state, addr.pincode].filter(Boolean).join(", ")}</p>
                            ) : (<p className="mt-1 text-xs text-zinc-500">Lab visit</p>)}
                            {cobj.notes && <p className="mt-1 text-[11px] italic text-zinc-500">"{cobj.notes}"</p>}
                        </section>
                    </aside>
                </div>
            </AdminLayout>
        </AdminAuthGuard>
    );
};

export default AdminLabOrderDetail;
